import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { Country, State, City } from 'country-state-city';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';

const ALL = Country.getAllCountries();
const COUNTRY_OPTS = ALL.map((c) => ({ value: c.isoCode, label: `${c.flag} ${c.name}` }));
const isoForCountry = (name) => ALL.find((c) => c.name === name)?.isoCode || 'IN';

/**
 * Cascading Country → State → City dropdowns. Emits flat names
 * `{ country, state, city }` on every change. Reused in onboarding so the
 * location fields match the rest of the app (no free-text typos).
 */
const LocationFields = ({ value, onChange, testidPrefix = 'loc' }) => {
  const [st, setSt] = useState(() => {
    const countryIso = isoForCountry(value.country || 'India');
    const sIso = (State.getStatesOfCountry(countryIso) || []).find((s) => s.name === value.state)?.isoCode || '';
    return { country_iso: countryIso, state_iso: sIso, city: value.city || '' };
  });

  const states = State.getStatesOfCountry(st.country_iso) || [];
  const cities = st.state_iso ? (City.getCitiesOfState(st.country_iso, st.state_iso) || []) : [];

  const set = (patch) => setSt((prev) => {
    const n = { ...prev, ...patch };
    const country = ALL.find((c) => c.isoCode === n.country_iso)?.name || '';
    const state = (State.getStatesOfCountry(n.country_iso) || []).find((s) => s.isoCode === n.state_iso)?.name || '';
    onChange({ country, state, city: n.city });
    return n;
  });

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Country</Label>
        <div className="mt-1.5"><Combobox testid={`${testidPrefix}-country`} options={COUNTRY_OPTS} value={st.country_iso} onChange={(v) => set({ country_iso: v, state_iso: '', city: '' })} searchPlaceholder="Search country" /></div>
      </div>
      <div>
        <Label className="text-xs">State</Label>
        <div className="mt-1.5"><Combobox testid={`${testidPrefix}-state`} options={states.map((s) => ({ value: s.isoCode, label: s.name }))} value={st.state_iso} onChange={(v) => set({ state_iso: v, city: '' })} placeholder="Select state" searchPlaceholder="Search state" emptyText="No states" /></div>
      </div>
      <div>
        <Label className="text-xs">City</Label>
        <div className="mt-1.5"><Combobox testid={`${testidPrefix}-city`} options={cities.map((c) => ({ value: c.name, label: c.name }))} value={st.city} onChange={(v) => set({ city: v })} placeholder={st.state_iso ? 'Select city' : 'Pick state first'} searchPlaceholder="Search city" emptyText="No cities" disabled={!st.state_iso} /></div>
      </div>
    </div>
  );
};

export default LocationFields;
